import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Wrapper simple para animar "reveal on scroll" sin dependencias.
 * - Usa IntersectionObserver
 * - Respeta prefers-reduced-motion via CSS
 */
export default function Reveal({
  children,
  className = '',
  delay = 0,
  /**
   * Tipos: 'fade-up' | 'fade-down' | 'slide-left' | 'slide-right' | 'zoom'
   */
  variant = 'fade-up',
  distance = 16,
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  const style = useMemo(
    () => {
      const d = Number(distance) || 16;

      let x = 0;
      let y = 0;
      let scale = 1;
      let rotate = 0;
      let blur = 4;

      switch (variant) {
        case 'fade-down':
          y = -d;
          break;
        case 'slide-left':
          x = -d;
          y = Math.round(d * 0.5);
          blur = 3;
          break;
        case 'slide-right':
          x = d;
          y = Math.round(d * 0.5);
          blur = 3;
          break;
        case 'zoom':
          scale = 0.965;
          y = Math.round(d * 0.5);
          blur = 2;
          break;
        case 'fade-up':
        default:
          y = d;
          break;
      }

      return {
        '--reveal-delay': `${delay}ms`,
        '--reveal-x': `${x}px`,
        '--reveal-y': `${y}px`,
        '--reveal-scale': `${scale}`,
        '--reveal-rotate': `${rotate}deg`,
        '--reveal-blur': `${blur}px`,
      };
    },
    [delay, distance, variant]
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Si el navegador no soporta IO, mostramos directo.
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal reveal--${variant}${visible ? ' is-visible' : ''}${className ? ` ${className}` : ''}`}
      style={style}
    >
      {children}
    </div>
  );
}
